import {
  Alert,
  Box,
  Button,
  Flex,
  Grid,
  Link,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
  Text,
  useDisclosure,
  useToast,
} from '@chakra-ui/react'
import type { Error, GetAvailableAddonOffersResponse } from 'BrokerTsLib/src/dtos'
import { OfferType } from 'BrokerTsLib/src/dtos'
import { ContractAcceptationType, Product } from 'BrokerTsLib/src/enums'
import * as global from 'brokertslib/src/helpers/global'
import { useInsuranceParamsFormPersistance } from 'BrokerTsLib/src/hooks'
import { FormProvider, useForm } from 'BrokerTsLib/src/rhf'
import React, { useEffect, useRef, useState } from 'react'
import { Link as RouterLink, useNavigate } from 'react-router-dom'

import { CustomWaypoint } from '@/components/CustomWaypoint'
import ErrorSummary from '@/components/ErrorSummary'
import { PageSection } from '@/components/PageSection'
import { RecapExtraInsurance } from '@/components/Recap/RecapExtraInsurance'
import { UnsavedFormModal } from '@/components/UnsavedFormModal'
import { FormSection } from '@/containers/forms/FormSection'
import { useInsuranceParams } from '@/context/InsuranceParamsProvider'
import { useMeetingRecord } from '@/context/MeetingRecordProvider'
import { usePartner } from '@/context/PartnerContext'
import { useSidebar } from '@/context/SidebarContext'
import { useGetAllDocuments } from '@/hooks/useDocuments'
import { useCreateContract } from '@/hooks/useWizardAddon'
import type { PageSection as Section } from '@/layouts/components/Sidebar'

import CommonMeetingRecordForm from '../../Step4/forms/CommonMeetingRecordForm'
import { CreateContractForm } from '../../Step4/forms/CreateContractForm'
import KapitolCreateContractForm from '../../Step4/forms/KapitolCreateContractForm'
import KapitolMeetingRecordForm from '../../Step4/forms/KapitolMeetingRecordForm'
import type { Step4FormValues } from '../../Step4/Step4'

export interface CalculationState {
  isCalculated: boolean
  isCalculating: boolean
}

export const Step4 = ({
  availableAddonOffers,
}: {
  availableAddonOffers: GetAvailableAddonOffersResponse[]
}) => {
  const { currentPartner } = usePartner()
  const { pageSections, setPageSections } = useSidebar()
  const [acceptChangedPrice, setAcceptChangedPrice] = useState(false)
  const [priceChanged, setPriceChanged] = useState(false)
  const [returnBack, setReturnBack] = useState(true)
  const [alerts, setAlerts] = useState<Error[]>([])
  const [shouldSubmit, setShouldSubmit] = useState(false)
  const {
    isOpen: isJapConfirmOpen,
    onClose: onJapConfirmClose,
    onOpen: onJapConfirmOpen,
  } = useDisclosure()
  const navigate = useNavigate()

  const {
    insuranceParamsId,
    refetchInsuranceParams,
    insuranceParams,
    isDisabled,
  } = useInsuranceParams()
  const {
    refetch: refetchMR,
    meetingRecord,
    save: saveMeetingRecord,
    isSaving: isSavingMeetingRecord,
  } = useMeetingRecord()

  const { refetch: refetchDocuments } = useGetAllDocuments(
    insuranceParamsId,
    insuranceParams!.generalParams.selectedOfferId!,
    { enabled: false },
  )
  const toast = useToast()

  // sjednani smlouvy
  const { mutateAsync: createContract } = useCreateContract()

  useEffect(() => {
    if (meetingRecord) reset({ meetingRecord })
  }, [!meetingRecord])

  const methods = useForm<Step4FormValues>({
    defaultValues: { meetingRecord },
    // resolver: yupResolver(schema),
    // mode: 'onBlur'
  })

  const {
    handleSubmit,
    reset,
    setError,
    getValues,
    formState: { errors, isSubmitting },
  } = methods

  const isValid = !Object.keys(errors).length

  let japConfirmBody = undefined
  if (insuranceParams?.policyHolder.person.email) {
    japConfirmBody = (
      <>
        <Text>
          Návrh smlouvy bude odeslán na email{' '}
          <strong>{insuranceParams?.policyHolder.person.email}</strong>
        </Text>
        <Text>Ověřte prosím správnost emailu před sjednáním.</Text>
      </>
    )
  }

  // handler na click sjednat pojisteni
  const handleSubmitClick = () => {
    setAcceptChangedPrice(priceChanged)
    const contractAcceptationType = getValues('meetingRecord.startType')
    if (
      currentPartner?.partner == 'Jap' &&
      contractAcceptationType == ContractAcceptationType.Payment
    ) {
      onJapConfirmOpen()
    } else {
      setShouldSubmit(true)
    }
  }

  // trigger submitu formulare
  useEffect(() => {
    if (shouldSubmit) {
      setShouldSubmit(false)
      submitForm()
    }
  }, [shouldSubmit])

  async function refetchAfterContractCreate() {
    // zřetězení refetch metod - možná má vliv na omezení výskytu chyby 'Cannot resolve 'register' v MeetingRecordForm nebo CreateContractForm'
    window.scrollTo(0, 0)
    await refetchInsuranceParams()
    await refetchMR()
  }

  const save = () => {
    const meetingRecordValue = getValues('meetingRecord')
    return saveMeetingRecord(meetingRecordValue)
      .then(() => {
        toast({
          status: 'success',
          title: 'Nabídka byla uložena.',
        })
      })
      .catch((err) => {
        if (err.response && err.response.status === 400 && err.response.data) {
          // Přidání prefixu meetingRecord, protože je použitý ve formuláři ale data se postují a vracejí bey něj
          Object.keys(err.response.data).forEach((x) => {
            err.response.data['meetingRecord.' + x] = err.response.data[x]
            delete err.response.data[x]
          })

          const data = err.response.data

          // zobrazeni serverove validace
          Object.keys(data).forEach((x: any) => {
            setError(global.toCamelCase(x), { message: data[x].join(' ') })
          })

          scrollToValidationSummary()
        } else if (err.response && err.response.status === 500) {
          toast({
            status: 'error',
            title: 'Nabídku se nepodařilo uložit.',
          })
        }

        return err
      })
  }

  const saveAndCreateContract = (values: Step4FormValues) => {
    return new Promise((resolve, reject) =>
      saveMeetingRecord(values.meetingRecord)
        .then(() => {
          insuranceParamsFormPersistance.clear()

          // sjednani pojistky
          createContract({ paramsId: insuranceParamsId!, acceptChangedPrice })
            .then((createContractResponse) => {
              if (
                createContractResponse.data.priceChanged &&
                !createContractResponse.data.contractCreated
              ) {
                setPriceChanged(true)
                setReturnBack(createContractResponse.data.returnBack)
                if (createContractResponse.data.errors.length > 0) {
                  setAlerts(createContractResponse.data.errors)
                  scrollToValidationSummary()
                  reject(createContractResponse.data.errors)
                }
              } else if (createContractResponse.data.errors.length > 0) {
                setAlerts(createContractResponse.data.errors)
                scrollToValidationSummary()
                reject([])
              } else {
                refetchAfterContractCreate()
                refetchDocuments()

                resolve(createContractResponse)
              }
            })
            .catch((err) => {
              reject(err)
            })
        })
        .catch((err) => {
          if (
            err.response &&
            err.response.status === 400 &&
            err.response.data
          ) {
            // Přidání prefixu meetingRecord, protože je použitý ve formuláři ale data se postují a vracejí bey něj
            Object.keys(err.response.data).forEach((x) => {
              err.response.data['meetingRecord.' + x] = err.response.data[x]
              delete err.response.data[x]
            })
          }

          reject(err)
        })
        .finally(() => { }),
    )
  }

  const validationSummaryRef = useRef<HTMLDivElement>(null)
  // v nevalidniho formulare se nascroluje na validationSummary
  const scrollToValidationSummary = () => {
    setTimeout(() => {
      validationSummaryRef.current?.scrollIntoView({ behavior: 'smooth' })
    }, 0)
  }


  const insuranceParamsFormPersistance = useInsuranceParamsFormPersistance(`${Product.VehicleInsurance}-extra`, 4, insuranceParamsId, methods)

  const submitForm = handleSubmit(
    (values) => {
      return saveAndCreateContract(values)
        .then(() => {
          reset(values)
          navigate(`/extra/steps/complete/${insuranceParamsId}`)
          // refetch se volá už po sjednání a případném odeslání emailu
          //refetchInsuranceParams()
        })
        .catch((err) => {
          if (
            !(err.response && err.response.status === 400 && err.response.data)
          )
            return

          const data = err.response.data

          // zobrazeni serverove validace
          Object.keys(data).forEach((x: any) => {
            setError(global.toCamelCase(x), { message: data[x].join(' ') })
          })

          scrollToValidationSummary()
        })
    },
    (err) => {
      scrollToValidationSummary()
    },
  )

  // registrace sekcí
  useEffect(() => {
    const array: Section[] = []
    let i = 1

    if (isDisabled) return

    if (availableAddonOffers) {
      // sekce Rekapitulace pojištění
      array.push({
        id: 'recapitulation',
        sectionTitle: 'Rekapitulace pojištění',
        index: i,
        children: (
          <CustomWaypoint sectionId="recapitulation">
            <PageSection id={'meetingRecord'}>
              <FormSection
                number={i++}
                sectionTitle={'Rekapitulace pojištění'}
                bodyProps={{
                  sx: { px: 0, pb: 0 },
                }}
              >
                <Stack pb={16}>
                  {availableAddonOffers.map(({ insurerId, risks }) => (
                    <RecapExtraInsurance
                      key={insurerId}
                      risks={risks}
                      insurerId={insurerId}
                      offerType={OfferType.Standard}
                    />
                  ))}
                </Stack>
              </FormSection>
            </PageSection>
          </CustomWaypoint>
        ),
      })
    }

    let partnerMeetingRecordForm = null
    let partnerArrangeContractForm = null
    switch (currentPartner!.partner) {
      case 'Kapitol':
        partnerMeetingRecordForm = <KapitolMeetingRecordForm />
        partnerArrangeContractForm = <KapitolCreateContractForm />
        break
      default:
        partnerMeetingRecordForm = <CommonMeetingRecordForm />
        partnerArrangeContractForm = <CreateContractForm />
        break
    }

    array.push({
      id: 'meetingRecord',
      sectionTitle: 'Záznam z jednání',
      index: i,
      children: (
        <CustomWaypoint sectionId="recapitulation">
          <PageSection id={'meetingRecord'}>
            <FormSection
              number={i++}
              sectionTitle={'Záznam z jednání'}
              wide={false}
            >
              {partnerMeetingRecordForm}
            </FormSection>
          </PageSection>
        </CustomWaypoint>
      ),
    })

    // sekce Sjednání
    array.push({
      id: 'contract',
      sectionTitle: 'Sjednání',
      index: i,
      useFormSection: true,
      children: (
        <CustomWaypoint sectionId="contract">
          <PageSection id={'contract'}>
            <FormSection number={i++} sectionTitle={'Sjednání'} wide={false}>
              <Grid rowGap={20}>
                {!!partnerArrangeContractForm && partnerArrangeContractForm}

                <Stack gap={8}>
                  <hr></hr>

                  <Flex justifyContent="center" w="100%" gap={4}>
                    <Button
                      loadingText="Ukládám nabídku..."
                      isLoading={isSavingMeetingRecord}
                      onClick={() => save()}
                      variant="solid"
                      colorScheme="primary"
                      isDisabled={isSubmitting || !isValid || isDisabled}
                    >
                      Uložit nabídku
                    </Button>
                    <Button
                      colorScheme="green"
                      disabled={isSubmitting || !isValid || isDisabled}
                      isLoading={isSubmitting && isValid}
                      loadingText="Sjednávám..."
                      type="button"
                      variant="solid"
                      onClick={handleSubmitClick}
                    >
                      {!priceChanged
                        ? 'Sjednat pojištění'
                        : 'Sjednat pojištění se změnou ceny'}
                    </Button>
                  </Flex>
                </Stack>
              </Grid>
            </FormSection>
          </PageSection>
        </CustomWaypoint>
      ),
    })

    setPageSections(array)

    return () => {
      setPageSections([])
    }
  }, [availableAddonOffers, isSubmitting, isValid, isDisabled])

  return (
    <FormProvider {...methods}>
      <form onSubmit={submitForm}>
        <Grid gap={8}>
          {pageSections.map((x) => (
            <React.Fragment key={x.id}>{x.children}</React.Fragment>
          ))}

          <Stack ref={validationSummaryRef}>
            {(priceChanged || alerts.length > 0) && (
              <Alert colorScheme="red" mb={8}>
                <Stack direction="column">
                  {priceChanged && (
                    <Text>
                      Před sjednáním došlo ke změně ceny.
                      {returnBack && (
                        <>
                          {' '}
                          Vraťte se na{' '}
                          <Link
                            as={RouterLink}
                            colorScheme="blue"
                            to={`/steps/2/${insuranceParamsId!}`}
                          >
                            &nbsp;seznam kalkulací&nbsp;
                          </Link>{' '}
                          a vybranou nabídku překalkulujte.
                        </>
                      )}
                    </Text>
                  )}

                  {alerts.length > 0 &&
                    alerts
                      .filter((x) => !!x.message)
                      .map((alert) => (
                        <Text
                          key={alert.message}
                          dangerouslySetInnerHTML={{
                            __html: alert.message ?? '',
                          }}
                        />
                      ))}

                  {!alerts?.length && <Text>
                    Při sjednávání došlo k chybě při komunikaci s pojišťovnou.
                  </Text>}
                </Stack>
              </Alert>
            )}

            <ErrorSummary errors={errors} />
          </Stack>

          <Modal isOpen={isJapConfirmOpen} onClose={onJapConfirmClose}>
            <ModalOverlay />
            <ModalContent>
              <ModalCloseButton />
              <ModalHeader>Sjednat pojištění</ModalHeader>
              <ModalBody>
                <Box sx={{ paddingBottom: 4 }}>
                  <Text>
                    Skutečně chcete sjednat pojištění k této nabídce a odeslat
                    email klientovi?
                  </Text>
                  {japConfirmBody && japConfirmBody}
                </Box>
                <Flex
                  sx={{
                    width: '100%',
                    alignItems: 'center',
                    gap: 4,
                    justifyContent: 'center',
                  }}
                >
                  <Button
                    colorScheme="green"
                    onClick={() => {
                      onJapConfirmClose()
                      setShouldSubmit(true)
                    }}
                  >
                    Ano
                  </Button>
                  <Button
                    colorScheme="gray"
                    onClick={() => onJapConfirmClose()}
                  >
                    Ne
                  </Button>
                </Flex>
              </ModalBody>
              <ModalFooter></ModalFooter>
            </ModalContent>
          </Modal>

          <UnsavedFormModal {...insuranceParamsFormPersistance} />
        </Grid>
      </form>
    </FormProvider>
  )
}
